/**
 * In-memory fixtures for the employer chat UI — enough variety to exercise the
 * conversation surface (day separators, read receipts, the reward nudge, a
 * job-bounded vs direct chat) without a backend. Relative timestamps so nothing
 * goes stale. Ported/condensed from the mock's `infrastructure/memory/fixtures`;
 * grows toward the full ~28-channel matrix when the inbox lands.
 */
import type { Channel, CtaPayload, Message, Participant } from './domain.js';

export const EMPLOYER_ID = 'emp_sarah';

/**
 * Fixed opener suggestions shown as quick-reply chips. The mock generates these
 * from the transcript with AI (`useQuickReplies`), falling back to this same
 * list; the UI-only rewrite serves the list directly. Replaced by the AI path
 * in the wiring epic.
 */
export const QUICK_REPLIES = [
  'Thanks for applying!',
  'Are you available for a quick call?',
  'Could you share your expected salary?',
  "We'd like to schedule an interview.",
];

const now = Date.now();
const minutesAgo = (m: number): string => new Date(now - m * 60_000).toISOString();
const hoursAgo = (h: number): string => minutesAgo(h * 60);
const daysAgo = (d: number): string => hoursAgo(d * 24);
const daysFromNow = (d: number): string => new Date(now + d * 24 * 60 * 60_000).toISOString();

export const participants: Participant[] = [
  {
    id: EMPLOYER_ID,
    name: 'Sarah Lim',
    role: 'employer',
    headline: 'Talent Acquisition · Acme Corp',
    online: true,
  },
  {
    id: 'js_aisha',
    name: 'Aisha Rahman',
    role: 'jobseeker',
    headline: 'Frontend Developer',
    online: true,
    locked: true,
    profile: {
      gender: 'Female',
      age: 29,
      domicile: 'Petaling Jaya, Selangor, Malaysia',
      nationality: 'Malaysian',
      experienceYears: 6,
      experiences: [
        { title: 'Frontend Developer', company: 'Fave', period: '2021 - Present' },
        { title: 'UI Engineer', company: 'Grab', period: '2019 - 2021' },
        { title: 'Junior Developer', company: 'Setel', period: '2018 - 2019' },
      ],
      skills: ['React', 'TypeScript', 'Next.js', 'CSS'],
      expectedSalary: 'RM 9,000',
      languages: ['Malay', 'English'],
    },
  },
  {
    id: 'js_farid',
    name: 'Farid Reza',
    role: 'jobseeker',
    headline: 'Full-Stack Engineer',
    online: false,
    locked: true,
    profile: {
      gender: 'Male',
      age: 34,
      experienceYears: 8,
      experiences: [
        { title: 'Senior Software Engineer', company: 'PayForward', period: '2020 - Present' },
        { title: 'Backend Engineer', company: 'iPay88', period: '2017 - 2020' },
      ],
      skills: ['Go', 'Kubernetes', 'React', 'PostgreSQL'],
      expectedSalary: 'RM 14,000',
      languages: ['Malay', 'English'],
    },
  },
  {
    id: 'js_daniel',
    name: 'Daniel Tan',
    role: 'jobseeker',
    headline: 'Backend Engineer',
    online: true,
  },
  {
    id: 'js_mei',
    name: 'Mei Ling',
    role: 'jobseeker',
    headline: 'Product Designer',
    online: false,
  },
  {
    id: 'js_tariq',
    name: 'Tariq Salleh',
    role: 'jobseeker',
    headline: 'Data Analyst',
    online: false,
  },
  {
    id: 'js_hana',
    name: 'Hana Yusof',
    role: 'jobseeker',
    headline: 'DevOps Engineer',
    online: true,
  },
];

export const channels: Channel[] = [
  {
    id: 'ch-aisha',
    memberIds: [EMPLOYER_ID, 'js_aisha'],
    counterpartId: 'js_aisha',
    lastMessageText: 'Sure, Tuesday afternoon works for me!',
    lastSenderId: 'js_aisha',
    lastActivityAt: hoursAgo(2),
    unreadCount: 0,
    unrepliedCount: 0,
    firstReplyPending: false,
    phase: 'open',
    applicationStatus: 'shortlisted',
    jobMeta: {
      jobId: 'job-fe',
      jobTitle: 'Senior Frontend Developer',
      jobType: 'Engineering',
      jobLocation: 'Kuala Lumpur (Hybrid)',
      description:
        'Build and own the employer-facing dashboards that thousands of recruiters use every day.',
      responsibilities: [
        'Ship polished, accessible UI in React + TypeScript',
        'Partner with design on a shared component system',
      ],
      requirements: ['4+ years with React', 'Strong CSS fundamentals', 'An eye for detail'],
    },
  },
  {
    id: 'ch-farid',
    memberIds: [EMPLOYER_ID, 'js_farid'],
    counterpartId: 'js_farid',
    lastMessageText: 'When would be a good time to chat?',
    lastSenderId: 'js_farid',
    lastActivityAt: hoursAgo(5),
    unreadCount: 2,
    unrepliedCount: 1,
    firstReplyPending: true,
    phase: 'open',
    applicationStatus: 'pending',
    jobMeta: {
      jobId: 'job-fs',
      jobTitle: 'Senior Full-Stack Engineer — Payments Platform',
      jobType: 'Engineering',
      jobLocation: 'APAC Remote',
    },
  },
  {
    id: 'ch-daniel',
    memberIds: [EMPLOYER_ID, 'js_daniel'],
    counterpartId: 'js_daniel',
    lastMessageText: 'Could you share your notice period for the Backend role?',
    lastSenderId: 'js_daniel',
    lastActivityAt: hoursAgo(7),
    unreadCount: 1,
    unrepliedCount: 1,
    firstReplyPending: true,
    phase: 'open',
    applicationStatus: 'pending',
    jobMeta: {
      jobId: 'job-be',
      jobTitle: 'Backend Engineer (Go)',
      jobType: 'Engineering',
      jobLocation: 'Kuala Lumpur',
    },
  },
  {
    id: 'ch-mei',
    memberIds: [EMPLOYER_ID, 'js_mei'],
    counterpartId: 'js_mei',
    lastMessageText: "Thank you! I'd love to hear more.",
    lastSenderId: 'js_mei',
    lastActivityAt: daysAgo(1),
    lastMessageHasAttachment: true,
    // Stamped by the backend because it is near the sweep. Most channels carry
    // no stamp and so show no countdown; that is the common case, not a gap.
    inactivityDeleteAt: daysFromNow(2),
    unreadCount: 0,
    unrepliedCount: 0,
    firstReplyPending: false,
    phase: 'open',
    applicationStatus: 'interview',
    jobMeta: {
      jobId: 'job-pd',
      jobTitle: 'Product Designer',
      jobType: 'Design',
      jobLocation: 'Remote',
    },
  },
  {
    id: 'ch-tariq',
    memberIds: [EMPLOYER_ID, 'js_tariq'],
    counterpartId: 'js_tariq',
    lastMessageText: 'Thanks for your time, Tariq — we won’t be moving ahead on this one.',
    lastSenderId: EMPLOYER_ID,
    lastActivityAt: daysAgo(2),
    // Ended but unread, so no 30-minute stamp yet — the idle clock is what is
    // still running on it.
    inactivityDeleteAt: daysFromNow(5),
    unreadCount: 0,
    unrepliedCount: 0,
    firstReplyPending: false,
    phase: 'closing',
    applicationStatus: 'rejected',
    jobMeta: {
      jobId: 'job-da',
      jobTitle: 'Data Analyst',
      jobType: 'Analytics',
      jobLocation: 'Kuala Lumpur',
    },
  },
  {
    id: 'ch-hana',
    memberIds: [EMPLOYER_ID, 'js_hana'],
    counterpartId: 'js_hana',
    lastMessageText: 'Thanks for reaching out! Yes, happy to chat.',
    lastSenderId: 'js_hana',
    lastActivityAt: hoursAgo(3),
    unreadCount: 0,
    unrepliedCount: 0,
    firstReplyPending: false,
    phase: 'open',
  },
  {
    id: 'ch-info',
    memberIds: [EMPLOYER_ID, 'marketing_ajt'],
    counterpartId: 'marketing_ajt',
    source: 'marketing_campaign',
    lastMessageText: 'New: Walk-In Interview events are now live on your dashboard.',
    lastSenderId: 'marketing_ajt',
    lastActivityAt: daysAgo(3),
    unreadCount: 1,
    unrepliedCount: 0,
    firstReplyPending: false,
    phase: 'open',
  },
];

const CTA_ENDPOINT = '/api/chat/cta-actions';

/**
 * Sent BY the employer, which is the whole point of seeding it here. Sarah sees
 * the row she offered Aisha and cannot press any of it, with no exclusion list
 * involved: the sender of a normal message is excluded from their own row,
 * always. This is the only place the rule is visible in the UI-only build.
 */
const CTA_CALL_OFFER: CtaPayload = {
  completed: false,
  buttons: [
    {
      id: 'book-call',
      label: 'Book a call',
      style: 'primary',
      mode: 'url',
      url: CTA_ENDPOINT,
      method: 'POST',
      payload: { applicationId: 8813 },
    },
    {
      id: 'ask-availability',
      label: 'Share your availability',
      style: 'secondary',
      mode: 'reply',
      reply: "I'm free Tuesday and Wednesday afternoon.",
    },
  ],
};

/** All three modes and all three styles on one row, on a broadcast Sarah can
 *  actually use. */
const CTA_HIRING_REPORT: CtaPayload = {
  completed: false,
  buttons: [
    {
      id: 'view-report',
      label: 'View full report',
      style: 'primary',
      mode: 'url',
      url: CTA_ENDPOINT,
      method: 'POST',
      payload: { reportId: 'rep-2026-07' },
    },
    {
      id: 'email-report',
      label: 'Email it to me',
      style: 'secondary',
      mode: 'event',
      payload: { channel: 'email' },
    },
    {
      id: 'stop-reports',
      label: 'Stop these updates',
      style: 'danger',
      mode: 'reply',
      reply: 'Please stop sending me the monthly hiring report.',
    },
  ],
};

/**
 * Five choices, so three show and the rest fold. The last carries a `reply` and
 * NO `mode` — exactly how campaign tooling authors these, and the case
 * `ctaModeOf` exists for.
 */
const CTA_CAMPAIGN: CtaPayload = {
  completed: false,
  buttons: [
    {
      id: 'boost-all',
      label: 'Boost all my jobs',
      style: 'primary',
      mode: 'reply',
      reply: 'Please boost all of my active jobs.',
    },
    {
      id: 'boost-urgent',
      label: 'Only the urgent ones',
      mode: 'reply',
      reply: 'Just boost the urgent roles for now.',
    },
    { id: 'see-pricing', label: 'See pricing first', mode: 'url', url: CTA_ENDPOINT },
    { id: 'ask-reach', label: 'How many people see it?', mode: 'event' },
    {
      id: 'not-this-time',
      label: 'Not this time',
      style: 'danger',
      mode: 'reply' as const,
      reply: "We'll sit this campaign out, thanks.",
    },
  ],
};

/** Already answered, so the row is spent: the chosen button keeps its ink and
 *  the rest fade. */
const CTA_CREDITS_ANSWERED: CtaPayload = {
  completed: true,
  completedActionId: 'top-up',
  completedAt: daysAgo(2),
  buttons: [
    { id: 'top-up', label: 'Top up credits', style: 'primary', mode: 'url', url: CTA_ENDPOINT },
    { id: 'remind-later', label: 'Remind me later', style: 'secondary', mode: 'event' },
  ],
};

/** The snapshot riding the seeded form invite — the Quick screening library
 *  entry as it was sent (see `FormNotice`; a snapshot, so it needs no import). */
const FORM_FIXTURE_FIELDS = [
  {
    id: 'q-start',
    label: 'When can you start?',
    type: 'choice' as const,
    options: [
      { id: 'o-now', label: 'Immediately' },
      { id: 'o-2w', label: 'Within 2 weeks' },
      { id: 'o-1m', label: 'Within a month' },
      { id: 'o-more', label: 'More than a month' },
    ],
  },
  {
    id: 'q-where',
    label: 'How would you like to work?',
    type: 'choice' as const,
    options: [
      { id: 'o-onsite', label: 'On site' },
      { id: 'o-hybrid', label: 'Hybrid' },
      { id: 'o-remote', label: 'Remote only' },
    ],
  },
  {
    id: 'q-extras',
    label: 'Which of these apply to you?',
    type: 'multiChoice' as const,
    options: [
      { id: 'o-transport', label: 'I have my own transport' },
      { id: 'o-travel', label: 'I can travel for work' },
      { id: 'o-weekends', label: 'I can work weekends' },
      { id: 'o-none', label: 'None of these' },
    ],
  },
];

/** Messages per channel, oldest first. Staggered minutes so every timestamp —
 *  and therefore every message id — is distinct. */
export const messages: Record<string, Message[]> = {
  'ch-aisha': [
    m(
      'ch-aisha',
      EMPLOYER_ID,
      'Hi Aisha, thanks for applying to the Senior Frontend role! Your profile looks like a strong fit.',
      minutesAgo(1595),
      'read',
    ),
    m(
      'ch-aisha',
      'js_aisha',
      "Thank you, Sarah! I'm really excited about the role.",
      minutesAgo(1588),
      'read',
    ),
    m(
      'ch-aisha',
      EMPLOYER_ID,
      'Would you be open to a short call this week?',
      minutesAgo(1580),
      'read',
      CTA_CALL_OFFER,
    ),
    m(
      'ch-aisha',
      'js_aisha',
      "Absolutely — I'm free Tuesday and Wednesday afternoons.",
      minutesAgo(205),
      'read',
    ),
    m(
      'ch-aisha',
      EMPLOYER_ID,
      "Great — let's do Tuesday 3pm. I'll send an invite.",
      minutesAgo(130),
      'read',
    ),
    m('ch-aisha', 'js_aisha', 'Sure, Tuesday afternoon works for me!', minutesAgo(124), 'read'),
  ],
  'ch-farid': [
    m(
      'ch-farid',
      'js_farid',
      "Hi Sarah — I've shipped payment services in Go on Kubernetes before, keen on this one.",
      minutesAgo(306),
      'read',
    ),
    m('ch-farid', 'js_farid', 'When would be a good time to chat?', minutesAgo(300), 'read'),
  ],
  'ch-daniel': [
    m(
      'ch-daniel',
      EMPLOYER_ID,
      'Hi Daniel, thanks for applying to the Backend Engineer role.',
      hoursAgo(9),
      'read',
    ),
    m(
      'ch-daniel',
      'js_daniel',
      'Could you share your notice period for the Backend role?',
      hoursAgo(7),
      'read',
    ),
    // A form sent by hand and answered, mid-thread: the invite card (sealed),
    // the answer card quoting it, and a reply after — the states both review
    // surfaces are drawn against. Ids follow m()'s `${channelId}-${createdAt}`.
    {
      ...m(
        'ch-daniel',
        EMPLOYER_ID,
        'A few quick questions before we go further, if you have a minute.',
        hoursAgo(6),
        'read',
      ),
      form: {
        formId: 'form-quick',
        name: 'Quick screening',
        fields: FORM_FIXTURE_FIELDS,
        answeredAt: hoursAgo(5),
        responseMessageId: `ch-daniel-${hoursAgo(5)}`,
      },
    },
    {
      ...m('ch-daniel', 'js_daniel', 'Answered Quick screening.', hoursAgo(5), 'read'),
      quotedMessageId: `ch-daniel-${hoursAgo(6)}`,
      formResponse: {
        id: 'resp-daniel-quick',
        formId: 'form-quick',
        noticeMessageId: `ch-daniel-${hoursAgo(6)}`,
        answers: [
          { fieldId: 'q-start', optionIds: ['o-2w'] },
          { fieldId: 'q-where', optionIds: ['o-hybrid'] },
          { fieldId: 'q-extras', optionIds: ['o-transport', 'o-weekends'] },
        ],
        submittedAt: hoursAgo(5),
      },
    },
    m(
      'ch-daniel',
      EMPLOYER_ID,
      'Perfect — two weeks works. Let me line up a call.',
      hoursAgo(4),
      'read',
    ),
  ],
  'ch-mei': [
    m(
      'ch-mei',
      EMPLOYER_ID,
      'Hi Mei, your case study for the Product Designer role was excellent.',
      minutesAgo(1500),
      'read',
    ),
    m('ch-mei', 'js_mei', "Thank you! I'd love to hear more.", minutesAgo(1440), 'read'),
    {
      ...m('ch-mei', 'js_mei', 'Here is my portfolio.', minutesAgo(1430), 'read'),
      attachments: [
        {
          type: 'image' as const,
          url: 'https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=600',
          name: 'portfolio.png',
        },
      ],
    },
    {
      ...m('ch-mei', 'js_mei', '', minutesAgo(1425), 'read'),
      attachments: [
        { type: 'file' as const, url: 'https://example.com/cv.pdf', name: 'mei-ling-cv.pdf' },
      ],
    },
  ],
  'ch-tariq': [
    m(
      'ch-tariq',
      'js_tariq',
      'Happy to walk through my dashboards whenever suits your team.',
      minutesAgo(2950),
      'read',
    ),
    m(
      'ch-tariq',
      EMPLOYER_ID,
      'Thanks for your time, Tariq — we won’t be moving ahead on this one.',
      minutesAgo(2880),
      'read',
    ),
  ],
  'ch-hana': [
    m(
      'ch-hana',
      EMPLOYER_ID,
      "Hi Hana, I came across your profile — we're expanding our platform team and thought you'd be a great fit. Open to a chat?",
      minutesAgo(200),
      'read',
    ),
    m(
      'ch-hana',
      'js_hana',
      'Thanks for reaching out! Yes, happy to chat.',
      minutesAgo(185),
      'read',
    ),
  ],
  'ch-info': [
    m(
      'ch-info',
      'marketing_ajt',
      'Your monthly hiring report is ready — 128 profile views across your active jobs.',
      daysAgo(5),
      'read',
      CTA_HIRING_REPORT,
    ),
    m(
      'ch-info',
      'marketing_ajt',
      "Raya campaign: we've boosted visibility for your active jobs.",
      daysAgo(4),
      'read',
      CTA_CAMPAIGN,
    ),
    m(
      'ch-info',
      'marketing_ajt',
      'Your job credits are running low.',
      daysAgo(2),
      'read',
      CTA_CREDITS_ANSWERED,
    ),
    m(
      'ch-info',
      'marketing_ajt',
      'New: Walk-In Interview events are now live on your dashboard.',
      daysAgo(3),
      'read',
    ),
  ],
};

function m(
  channelId: string,
  senderId: string,
  text: string,
  createdAt: string,
  status: Message['status'],
  cta?: CtaPayload,
): Message {
  return {
    id: `${channelId}-${createdAt}`,
    channelId,
    senderId,
    text,
    createdAt,
    status,
    system: false,
    ...(cta ? { cta } : {}),
  };
}
